// Demo audit trail, as ledger.audit_log_list / audit_people (db/027_audit_month_end.sql):
// who changed what and when, newest first, with each field before and after.
import type { AuditEntry, AuditFilter, AuditPage, AuditPerson, LedgerSource } from './source';

type Part = Pick<LedgerSource, 'auditTrail' | 'auditPeople'>;
const delay = <T,>(v: T, ms = 60): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));

const PEOPLE: Record<string, string> = {
  'ledger:user:brian': 'Brian',
  'ledger:user:aoife': 'Aoife Byrne',
  'ledger:user:declan': 'Declan Walsh',
  'client:certtrack': 'CertTrack',
  system: 'Elysian (automatic)',
};

type Seed = Omit<AuditEntry, 'id' | 'actor_name'>;
const e = (at: string, actor: string, kind: AuditEntry['kind'], action: AuditEntry['action'], label: string,
  changes: AuditEntry['changes'], entity_id: string | null = null): Seed => ({ at, actor, kind, action, label, changes, entity_id });

function seed(): AuditEntry[] {
  const list: Seed[] = [
    e('2026-09-25T10:02:00Z', 'ledger:user:brian', 'user', 'created', 'Declan Walsh',
      { role: { to: 'accountant' }, email: { to: 'declan@walshaccountants.ie' }, firm: { to: 'Walsh & Co Accountants' }, access_ends_at: { to: '2027-03-31T23:59:59Z' } },
      'ledger:user:declan'),
    e('2026-09-28T09:14:00Z', 'ledger:user:brian', 'settings', 'changed', 'Blaze Fire Systems',
      { contact_phone: { from: '01 555 0100', to: '01 555 0199' } }),
    e('2026-09-30T16:40:00Z', 'ledger:user:aoife', 'document', 'posted', 'BILL-309 · Detector Wholesale Ltd · DW-88907',
      { doc_no: { to: 'BILL-309' }, status: { from: 'draft', to: 'posted' } }),
    e('2026-10-01T06:00:00Z', 'system', 'document', 'created', 'Draft invoice · Swords Pavilions',
      { status: { to: 'draft' }, contact: { to: 'Swords Pavilions' }, issue_date: { to: '2026-10-01' }, net_total: { to: 85 }, tax_total: { to: 11.48 }, reference: { to: 'Monitoring October 2026' } }),
    e('2026-10-01T11:30:00Z', 'ledger:user:brian', 'document', 'posted', 'INV-000412 · Swords Pavilions',
      { doc_no: { to: 'INV-000412' }, status: { from: 'draft', to: 'posted' } }),
    e('2026-10-02T09:05:00Z', 'ledger:user:declan', 'journal', 'posted', 'J000881 · Accrue September electricity',
      { journal_no: { to: 'J000881' }, journal_date: { to: '2026-09-30' }, description: { to: 'Accrue September electricity' }, source: { to: 'manual' } }),
    e('2026-10-03T14:11:00Z', 'ledger:user:aoife', 'contact', 'changed', 'Dublin Hotels Group',
      { email: { from: 'accounts@dublinhotels.ie', to: 'finance@dublinhotels.ie' }, payment_terms_days: { from: 45, to: 30 } }, 'c4'),
    e('2026-10-05T08:55:00Z', 'client:certtrack', 'contact', 'created', 'Howth Yacht Club',
      { name: { to: 'Howth Yacht Club' }, is_customer: { to: true }, country: { to: 'IE' }, email: { to: 'office@hyc.ie' } }),
    e('2026-10-06T10:20:00Z', 'ledger:user:brian', 'product', 'changed', 'DET-OPT Optical smoke detector',
      { sales_price: { from: 41, to: 42.5 } }),
    e('2026-10-06T10:24:00Z', 'ledger:user:brian', 'account', 'changed', '7300 Telephone and internet',
      { name: { from: 'Telephone', to: 'Telephone and internet' } }),
    e('2026-10-07T12:30:00Z', 'ledger:user:aoife', 'document', 'voided', 'INV-000409 · Sandyford Retail Ltd',
      { status: { from: 'posted', to: 'void' } }),
    e('2026-10-09T09:48:00Z', 'ledger:user:brian', 'settings', 'changed', 'Bank details for 1200 AIB Current',
      { iban: { from: 'IE29 AIBK 9311 5212 3456 78', to: 'IE64 AIBK 9311 5299 1122 33' } }),
    e('2026-10-09T15:02:00Z', 'ledger:user:aoife', 'document', 'created', 'Draft bill · Screwfix Ireland · SFX-44102',
      { status: { to: 'draft' }, contact: { to: 'Screwfix Ireland' }, supplier_ref: { to: 'SFX-44102' }, issue_date: { to: '2026-10-08' }, net_total: { to: 64.2 }, tax_total: { to: 14.77 } }),
    e('2026-10-10T11:16:00Z', 'ledger:user:declan', 'journal', 'reversed', 'J000884 · Reversal of J000881: Accrual posted twice',
      { journal_no: { to: 'J000884' }, journal_date: { to: '2026-09-30' }, source: { to: 'reversal' } }),
    e('2026-10-11T09:12:00Z', 'ledger:user:brian', 'recurring', 'changed', 'Monitoring — Swords Pavilions',
      { mode: { from: 'auto_post', to: 'approve' } }, 't-mon'),
  ];
  return list.map((x, i) => ({ ...x, id: i + 1, actor_name: PEOPLE[x.actor] ?? x.actor }));
}

let entries = seed();
export function resetDemoAudit() { entries = seed(); }

/** For the other demo modules: record a change as the database's triggers would. */
export function recordDemoAudit(x: Omit<AuditEntry, 'id' | 'at' | 'actor' | 'actor_name'> & { actor?: string }) {
  const actor = x.actor ?? 'ledger:user:brian';
  entries = [...entries, { ...x, actor, actor_name: PEOPLE[actor] ?? actor, id: entries.length + 1, at: new Date().toISOString() }];
}

const day = (iso: string) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Dublin' }).format(new Date(iso));

export const demoAudit: Part = {
  auditTrail: (f: AuditFilter = {}) => {
    const limit = Math.min(Math.max(f.limit ?? 50, 1), 200);
    const q = f.q?.trim().toLowerCase();
    const rows = [...entries].reverse().filter((x) =>
      (!f.person || x.actor === f.person) && (!f.kind || x.kind === f.kind) && (!f.entity || x.entity_id === f.entity)
      && (!f.from || day(x.at) >= f.from) && (!f.to || day(x.at) <= f.to)
      && (!q || x.label.toLowerCase().includes(q)) && (f.before == null || x.id < f.before));
    const page = rows.slice(0, limit);
    const out: AuditPage = { entries: page, next_before: rows.length > limit ? page[page.length - 1]!.id : null };
    return delay(out);
  },
  auditPeople: () => {
    const seen = [...new Set(entries.map((x) => x.actor))];
    const people: AuditPerson[] = seen.map((actor) => ({ actor, name: PEOPLE[actor] ?? actor }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    return delay(people);
  },
};
